import PropTypes from "prop-types"
import React from "react"
import { Helmet } from "react-helmet"
import classNames from "classnames"
import { faChevronRight } from "@fortawesome/free-solid-svg-icons"

import CareServiceTypeList from "../CareServiceTypeList/CareServiceTypeList"
import { providerProfileURL } from "../../../../utils/paths"

import Availability from "../Availability/Availability"
import RegulatorsRating from "../../RegulatorsRating/RegulatorsRating"
import SmartLink from "../../../layout/SmartLink/SmartLink"
import AdditionalInfo from "./AdditionalInfo"
import ImageWithCount from "../ImageWithCount/ImageWithCount"
import Icon from "../../../layout/icon"
import { BodyText, InlineText, SubTitle } from "../../../Typography/Typography"
import { renderIf } from "../../../FeatureToggle/FeatureToggle"
import Separator from "../../../Separator/Separator"
import ButtonLink from "../../../ButtonLink/ButtonLink"
import {
  address as addressStyle,
  availability,
  cardLink,
  careProviderCard,
  footer,
  image,
  imageContainer,
  main,
  moreDetailsIcon,
  padLeftSide,
  phoneButton,
  row,
  side,
  sideWithImage,
  stackItem,
  table,
  tall,
  telButtonRow,
  townLabelWrapper,
} from "./styles.module.scss"
import { formatNumberAsGbp } from "../../AverageCareHomeCosts/PricePerWeek/PricePerWeek"
import CareTypeLabel from "./CareTypeLabel"
import TownLabel from "./TownLabel/TownLabel"
import { MediumAndUp, SmallOnly } from "../../../layout/Responsive/Responsive"
import { branchRecordType } from "../../../../shared-prop-types"
import useRatingCopy from "../../../../utils/useRatingCopy"

const determineCurrentUrl = (currentUrl) => {
  if (currentUrl) return { "Current URL": currentUrl }
  return undefined
}

const trimToWords = (numberOfWords) => (string) =>
  string.split(" ").slice(0, numberOfWords).concat(["…"]).join(" ")

const trimTo40Words = trimToWords(40)

const providerListItemClickEvent = (providerEventProperties) => ({
  name: "SEO - Filtered Directory - Provider List Item Clicked",
  properties: providerEventProperties,
})

const addressCopy = ({ streetAddress1, streetAddress2, city, postcode }) =>
  [streetAddress1, streetAddress2, city, postcode].filter((x) => x).join(", ")

const CareProviderCard = ({
  branchRecord,
  clickEventBaseProperties,
  index,
  currentUrl,
  showCareTypeLabel,
  showTownLabel,
  locationName,
}) => {
  const {
    name,
    regulatoryId,
    regulatoryBody,
    latestInspectionRating,
    address,
    careServiceTypes,
    careProvider,
    numberOfBeds,
    numberOfBedsInSingleRooms,
    numberOfBedsInDoubleRooms,
    numberOfBedsWithEnsuite,
    dateOpened,
    careType,
    phoneNumber,
  } = branchRecord
  const {
    photoCount,
    coverPhotoUrl,
    coverPhoto,
    currentAvailability,
    info,
    chStartingWeeklyRate,
    video,
    displayPhoneNumber,
  } = careProvider || {}
  const isCareHome = careType === "care_home"
  const isHomeCare = careType === "home_care"
  const RenderIfInfoSectionExists = renderIf(!!info)
  const infoText = !!info && trimTo40Words(info)
  const haveCoverPhoto = coverPhoto?.childImageSharp?.gatsbyImageData
  const url = providerProfileURL(regulatoryId, regulatoryBody, name)
  const cardPhoneNumber = displayPhoneNumber || phoneNumber
  const carePhoneNumberNoSpace = cardPhoneNumber?.replace(/\s/g, "")
  const descriptionForStructuredData = latestInspectionRating
    ? `${name} is located in ${locationName} and has been rated ${useRatingCopy(
        regulatoryBody,
        latestInspectionRating,
      )} by the ${regulatoryBody}`
    : `${name} is located in ${locationName}`

  const providerEventProperties = {
    ...clickEventBaseProperties,
    ...determineCurrentUrl(currentUrl),
    "Selected Branch Record ID": branchRecord.id,
    "Selected Branch Record Name": branchRecord.name,
    "Selected Branch Record Care Type": branchRecord.careType,
    "Selected Branch Record Position": index + 1,
    "Selected Branch Record Overall Rating":
      branchRecord.latestInspectionRating,
    "Selected Branch Record Has Logo":
      !!branchRecord.careProvider && !!branchRecord.careProvider.logoUrl,
    "Selected Branch Record Has Photos":
      !!branchRecord.careProvider && !!branchRecord.careProvider.photos,
  }
  let unclicked = true
  /* eslint-disable no-console */
  const handleClick = () => {
    // Register ProviderLogging::PhoneNumberClick event
    if (unclicked) {
      fetch(
        `https://www.caresourcer.com/uk/search/provider/${branchRecord.id}/phone_number_click`,
      )
        .then((response) => response.json())
        .then((unclicked = false))
        .catch((rejected) => {
          console.log(rejected)
        })
    }
  }

  return (
    <>
      <div className={classNames(careProviderCard)}>
        <div className={classNames(table)}>
          <SmallOnly className={townLabelWrapper}>
            {showTownLabel && <TownLabel town={address.city} />}
          </SmallOnly>
          <div className={classNames(main)}>
            <MediumAndUp className={townLabelWrapper}>
              {showTownLabel && <TownLabel town={address.city} />}
            </MediumAndUp>
            <div className={classNames(padLeftSide)}>
              {showCareTypeLabel && <CareTypeLabel careType={careType} />}
              <SubTitle bold noMarginBottom>
                {name}
              </SubTitle>
              {address && (
                <div className={classNames(stackItem, tall, addressStyle)}>
                  <InlineText light={isHomeCare}>
                    {isHomeCare && "Registered Office: "}
                    {addressCopy(address)}
                  </InlineText>
                </div>
              )}
              {regulatoryBody && (
                <div className={classNames(stackItem)}>
                  <RegulatorsRating
                    regulatoryBody={regulatoryBody}
                    rating={latestInspectionRating}
                    ratingLabel
                  />
                </div>
              )}
              {careServiceTypes && (
                <div className={classNames(stackItem)}>
                  <CareServiceTypeList
                    enabledCareServiceTypes={careServiceTypes}
                  />
                </div>
              )}
              {isCareHome && (
                <AdditionalInfo
                  numberOfBeds={numberOfBeds}
                  numberOfBedsInSingleRooms={numberOfBedsInSingleRooms}
                  numberOfBedsInDoubleRooms={numberOfBedsInDoubleRooms}
                  numberOfBedsWithEnsuite={numberOfBedsWithEnsuite}
                  dateOpened={dateOpened}
                  className={classNames(stackItem)}
                />
              )}
            </div>
          </div>
          <div
            className={classNames(side, {
              [sideWithImage]: haveCoverPhoto,
            })}
          >
            {haveCoverPhoto && (
              <ImageWithCount
                img={coverPhoto}
                alt={`${name} cover`}
                count={photoCount}
                hasVideo={video}
                classNames={{
                  image: classNames(image),
                  container: classNames(imageContainer),
                }}
              />
            )}
          </div>
          <div className={classNames(footer)}>
            <RenderIfInfoSectionExists>
              <Separator />
              <BodyText>{infoText}</BodyText>
            </RenderIfInfoSectionExists>
            <div className={classNames(row)}>
              {isCareHome && currentAvailability && (
                <Availability className={classNames(stackItem, availability)} />
              )}
            </div>
            <div className={telButtonRow}>
              <div
                role="button"
                onKeyDown={handleClick}
                tabIndex="0"
                onClick={handleClick}
              >
                <ButtonLink
                  to={`tel:${carePhoneNumberNoSpace}`}
                  outline
                  noChevron
                  centeredContent
                  className={classNames(phoneButton)}
                >
                  <>
                    <InlineText brand bold large>
                      {cardPhoneNumber}
                    </InlineText>
                  </>
                </ButtonLink>
              </div>
              <InlineText brand>
                {chStartingWeeklyRate ? (
                  <>
                    <InlineText>From</InlineText>{" "}
                    <InlineText brand bold large>
                      {formatNumberAsGbp(chStartingWeeklyRate)}
                    </InlineText>{" "}
                    <InlineText brand>/ week</InlineText>
                  </>
                ) : (
                  "More details"
                )}
                <Icon
                  icon={faChevronRight}
                  className={classNames(moreDetailsIcon)}
                />
              </InlineText>
            </div>
          </div>
        </div>
        <SmartLink
          className={classNames(cardLink)}
          to={url}
          clickEvent={providerListItemClickEvent(providerEventProperties)}
          noHoverStyle
        />
      </div>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            address: {
              "@type": "PostalAddress",
              streetAddress: address.streetAddress1,
              addressLocality: address.city,
              postalCode: address.postcode,
            },
            name,
            image: coverPhotoUrl,
            URL: url,
            description: descriptionForStructuredData,
          })}
        </script>
      </Helmet>
    </>
  )
}

CareProviderCard.propTypes = {
  branchRecord: branchRecordType.isRequired,
  clickEventBaseProperties: PropTypes.shape({
    name: PropTypes.string,
    properties: PropTypes.object,
  }),
  currentUrl: PropTypes.string,
  index: PropTypes.number.isRequired,
  showCareTypeLabel: PropTypes.bool,
  showTownLabel: PropTypes.bool,
  locationName: PropTypes.string.isRequired,
}

CareProviderCard.defaultProps = {
  currentUrl: undefined,
  clickEventBaseProperties: undefined,
  showCareTypeLabel: false,
  showTownLabel: false,
}

export default CareProviderCard
